import { makeStyles } from '@mui/styles'
import React, { useEffect, useState } from 'react'
import { Grid, CircularProgress } from '@mui/material'
import { useMutation, useQueryClient } from '@tanstack/react-query'

import {
  BaseDialog,
  StyledButton,
  UPLOAD_IMAGE_TYPES,
  createUppy,
} from 'shared'

import { API } from 'api'
import { updateProfile as updateProfileApi } from 'api/user'

import { withModal } from 'utils/providers/ModalProvider'

import { BaseModalComponentType, ModalNameToModalMap } from '../types'

import modals from 'constants/modals'
import { toast } from 'react-toastify'
import { useAuth } from 'utils/providers/AuthProvider'
import { Dashboard } from '@uppy/react'

const useStyles = makeStyles(() => {
  return {
    dialog: {
      '& .MuiPaper-root': {
        width: '700px',
      },
    },
    photo: {
      width: '100%',
      height: 'auto',
      maxHeight: '500px',
      objectFit: 'contain',
    },
    uploader: {
      '& .uppy-Dashboard-inner': {
        height: '400px !important',
      },
    },
  }
})

const UploadAvatarModal = ({
  closeModal,
  isOpen,
  payload,
}: BaseModalComponentType & {
  payload: ModalNameToModalMap['uploadAvatarModal']
}) => {
  const classes = useStyles()
  const client = useQueryClient()
  const { user: creator } = useAuth()
  const { showToast = true } = payload || {}

  const [image, setImage] = useState<{
    url?: string | null
    id: string | null
  }>({
    url: creator?.profile.profileImageUrl,
    id: null,
  })

  const updateProfile = useMutation({
    mutationFn: updateProfileApi,
    onSuccess: () => {
      client.invalidateQueries(['user'])
      if (showToast) {
        toast.success('Profile updated successfully')
      }
      closeModal()
    },
  })

  const [uppy] = useState(() => {
    const uppy = createUppy(API)

    uppy.setOptions({
      restrictions: {
        allowedFileTypes: UPLOAD_IMAGE_TYPES,
        maxNumberOfFiles: 1,
      },
    })

    uppy.on('complete', (data) => {
      const [file] = data.successful

      setImage({ url: decodeURIComponent(file.uploadURL), id: file.id })
    })

    return uppy
  })

  const deleteProfileImage = () => {
    updateProfile.mutate({ profileImageUrl: null })
  }

  const saveProfileImage = () => {
    updateProfile.mutate({ profileImageUrl: image.url })
  }

  useEffect(() => {
    if (isOpen) {
      return setImage({
        url: creator?.profile.profileImageUrl,
        id: null,
      })
    }
    if (image.id) {
      uppy.removeFile(image.id)
    }
    setImage({
      url: null,
      id: null,
    })
  }, [isOpen, uppy, creator])

  return (
    <BaseDialog
      open={isOpen}
      onClose={closeModal}
      title="Profile Photo"
      className={classes.dialog}
    >
      <Grid>
        {image.url ? (
          <img className={classes.photo} src={image.url} alt="" />
        ) : (
          <Dashboard uppy={uppy} className={classes.uploader} />
        )}

        <Grid
          mt={2}
          container
          justifyContent="flex-end"
          gap={2}
          alignItems="center"
        >
          {image.url && image.url === creator?.profile.profileImageUrl ? (
            <>
              <StyledButton
                variant="outlined"
                onClick={() => setImage({ url: null, id: null })}
              >
                Reset
              </StyledButton>
              <StyledButton
                color="error"
                variant="contained"
                onClick={deleteProfileImage}
                startIcon={
                  updateProfile.isLoading ? (
                    <CircularProgress size="1em" />
                  ) : null
                }
                disabled={updateProfile.isLoading}
              >
                Delete
              </StyledButton>
            </>
          ) : (
            <>
              <StyledButton
                color="light"
                variant="contained"
                onClick={() => closeModal()}
              >
                Cancel
              </StyledButton>
              <StyledButton
                color="mint"
                variant="contained"
                onClick={saveProfileImage}
                disabled={updateProfile.isLoading || !image.url}
                startIcon={
                  updateProfile.isLoading ? (
                    <CircularProgress size="1em" />
                  ) : null
                }
              >
                Save
              </StyledButton>
            </>
          )}
        </Grid>
      </Grid>
    </BaseDialog>
  )
}

export default withModal(modals.uploadAvatarModal)(UploadAvatarModal)
